import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";

// Not account-scoped and must survive logout, so this lives outside Redux/
// redux-persist entirely — a plain localStorage flag, with this context as
// the reactive layer so every consumer under it (the dashboard banner, each
// case card's note) updates in the same render pass when it's dismissed.
const IDENTITY_VERIFICATION_CONFIRMED_BANNER_DISMISSED_KEY =
  "identityVerificationConfirmedBannerDismissed";

const readDismissed = (): boolean =>
  localStorage.getItem(IDENTITY_VERIFICATION_CONFIRMED_BANNER_DISMISSED_KEY) === "true";

const IdentityVerificationConfirmedBannerContext = createContext<{
  isDismissed: boolean;
  dismiss: () => void;
}>({
  isDismissed: false,
  dismiss: () => {},
});

export const IdentityVerificationConfirmedBannerProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [isDismissed, setIsDismissed] = useState(readDismissed);

  const dismiss = useCallback(() => {
    localStorage.setItem(IDENTITY_VERIFICATION_CONFIRMED_BANNER_DISMISSED_KEY, "true");
    setIsDismissed(true);
  }, []);

  const value = useMemo(() => ({ isDismissed, dismiss }), [isDismissed, dismiss]);

  return (
    <IdentityVerificationConfirmedBannerContext.Provider value={value}>
      {children}
    </IdentityVerificationConfirmedBannerContext.Provider>
  );
};

export const useIdentityVerificationConfirmedBanner = () =>
  useContext(IdentityVerificationConfirmedBannerContext);
